import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import {
  Layout, Tree, Input, Button, Tag, Tabs, Empty, Spin, Progress,
  Drawer, Grid, Tooltip, App as AntdApp, Segmented, Table,
} from 'antd';
import {
  Plus, Search, Briefcase, Users, Calendar,
  CheckCircle2, AlertCircle, Loader, Menu as MenuIcon,
  Folder, FolderOpen, FolderTree, ChevronsDownUp,
  LayoutGrid, List as ListIcon,
} from 'lucide-react';
import dayjs from 'dayjs';
import { projectService, type ProjectListItem, type ProjectStatus } from '@/services/projectService';
import { orgService } from '@/services/rolesService';
import { CreateProjectModal } from '@/components/projects/CreateProjectModal';
import { EditProjectModal } from '@/components/projects/EditProjectModal';
import { ProjectDetailPanel } from '@/components/projects/ProjectDetailPanel';
import { useAuthStore } from '@/store/authStore';

const { Sider, Content } = Layout;

/**
 * Trang Quản lý dự án — UI 2 cột:
 * - Sider trái: tree department + projects của department đó
 * - Content phải: chi tiết project được chọn (header + tabs + tasks)
 *
 * Routing:
 * - /projects → trang chính, chưa chọn project nào → hiển thị overview tất cả
 * - Click vào 1 project trong tree → hiển thị detail panel ở content
 * - Navigate(/projects, {state: {selectProjectId}}) → tự select project (vd sau khi tạo task)
 */
export function ProjectsPage() {
  const { message } = AntdApp.useApp();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Permission tạo project: SYS_ADMIN, BOD, DEPT_HEAD
  const hasAnyRole = useAuthStore(s => s.hasAnyRole);
  const canCreateProject = hasAnyRole(['SYS_ADMIN', 'BOD', 'DEPT_HEAD']);
  // V1.0.36: User hiện tại để check quyền sửa project (owner mới được sửa, ngoại trừ admin)
  const currentUser = useAuthStore(s => s.user);
  const isAdmin = hasAnyRole(['SYS_ADMIN']);

  const [createOpen, setCreateOpen] = useState(false);
  // V1.0.36: Edit project modal — chỉ owner/manager/admin
  const [editOpen, setEditOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    () => location.state?.selectProjectId ?? null
  );
  const [searchKeyword, setSearchKeyword] = useState('');

  // V1.0.36: selectedStatus persisted to localStorage (tab Đang chạy / Đã hoàn thành)
  // 'ARCHIVED' không cache vào đây — vì tab Lưu trữ có UI riêng
  const [selectedStatus, setSelectedStatus] = useState<ProjectStatus | undefined>(() => {
    const saved = localStorage.getItem('hv-projects-status-tab');
    if (saved === 'ACTIVE' || saved === 'COMPLETED' || saved === 'DRAFT' || saved === 'ON_HOLD') {
      return saved as ProjectStatus;
    }
    return undefined; // = "tất cả (trừ archived)"
  });
  useEffect(() => {
    if (selectedStatus) {
      localStorage.setItem('hv-projects-status-tab', selectedStatus);
    } else {
      localStorage.removeItem('hv-projects-status-tab');
    }
  }, [selectedStatus]);

  // Load tree department (cấp 1) + projects (cấp 2)
  const { data: orgTree = [] } = useQuery({
    queryKey: ['org-tree'],
    queryFn: () => orgService.getTree(),
  });

  // V1.0.32: Load danh sách nhóm khoa phòng để thêm 1 cấp group
  const { data: deptGroups = [] } = useQuery({
    queryKey: ['dept-groups'],
    queryFn: () => orgService.listGroups(),
  });

  // V1.0.32: Tree mode — true = gom theo nhóm khoa phòng (Nhóm → Khoa → Dự án)
  // V1.0.33: Mặc định BẬT gom theo nhóm
  const [groupByDeptGroup, setGroupByDeptGroup] = useState<boolean>(() => {
    const saved = localStorage.getItem('hv-projects-tree-group');
    // Nếu user chưa từng set → default true. Nếu set '0' → false. Set '1' → true.
    if (saved === null) return true;
    return saved === '1';
  });
  useEffect(() => {
    localStorage.setItem('hv-projects-tree-group', groupByDeptGroup ? '1' : '0');
  }, [groupByDeptGroup]);

  // V1.0.36: Expand/collapse state — null = mặc định expand all, [] = collapse all, [keys] = các key đang mở
  // Cache mảng keys vào localStorage để user không phải mở lại từng node mỗi lần
  const [expandedKeys, setExpandedKeys] = useState<React.Key[] | null>(() => {
    const saved = localStorage.getItem('hv-projects-expanded-keys');
    if (saved === null) return null;
    if (saved === '__none__') return [];
    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : null;
    } catch { return null; }
  });
  useEffect(() => {
    if (expandedKeys === null) {
      localStorage.removeItem('hv-projects-expanded-keys');
    } else if (expandedKeys.length === 0) {
      localStorage.setItem('hv-projects-expanded-keys', '__none__');
    } else {
      localStorage.setItem('hv-projects-expanded-keys', JSON.stringify(expandedKeys));
    }
  }, [expandedKeys]);

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', selectedStatus, searchKeyword],
    queryFn: () => projectService.list({
      status: selectedStatus,
      keyword: searchKeyword || undefined,
    }),
  });

  // Build tree data: department node có children là projects của department đó
  // V1.0.32: Nếu groupByDeptGroup = true → thêm 1 cấp Nhóm trên cùng
  const treeData = useMemo(() => {
    const projectsByDept = new Map<string | null, ProjectListItem[]>();
    for (const p of projects) {
      const k = p.departmentId ?? '__no_dept__';
      if (!projectsByDept.has(k)) projectsByDept.set(k, []);
      projectsByDept.get(k)!.push(p);
    }

    const buildDeptNode = (dept: any): any => {
      const projs = projectsByDept.get(dept.id) ?? [];
      const deptTaskTotal = projs.reduce((sum, p) => sum + p.taskCount, 0);
      return {
        key: `dept-${dept.id}`,
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <Briefcase size={14} style={{ color: '#5b6875', flexShrink: 0 }} />
            <span style={{ fontWeight: 600, flex: 1, color: '#1a1e23', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dept.name}
            </span>
            {dept.groupName && !groupByDeptGroup && (
              <span style={{
                fontSize: 9, padding: '0 6px', borderRadius: 8,
                background: (dept.groupColor ?? '#e8edf2') + '20',
                color: dept.groupColor ?? '#5b6875',
                border: `1px solid ${dept.groupColor ?? '#e8edf2'}40`,
                flexShrink: 0,
              }}>
                {dept.groupName}
              </span>
            )}
            {deptTaskTotal > 0 && (
              <span style={{
                background: '#1976D2', color: '#fff', borderRadius: 10,
                padding: '1px 8px', fontSize: 11, minWidth: 22, textAlign: 'center',
                fontWeight: 600,
              }}>
                {deptTaskTotal}
              </span>
            )}
          </span>
        ),
        selectable: false,
        deptName: dept.name,
        deptId: dept.id,
        children: projs.map(p => ({
          key: `prj-${p.id}`,
          title: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: p.color ?? '#1976D2', flexShrink: 0,
                display: 'inline-block',
              }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name}
              </span>
              {p.taskCount > 0 && (
                <span style={{
                  background: '#f0f4f8', color: '#5b6875',
                  borderRadius: 10, padding: '1px 6px',
                  fontSize: 11, minWidth: 18, textAlign: 'center',
                  fontWeight: 500,
                }}>
                  {p.taskCount}
                </span>
              )}
            </span>
          ),
          isLeaf: true,
          projectId: p.id,
          deptName: dept.name,
        })),
      };
    };

    // V1.0.32: Mode group-dept-project (Nhóm → Khoa → Dự án)
    if (groupByDeptGroup) {
      // Flatten depts
      const flatDepts: any[] = [];
      const walkOrg = (n: any) => {
        flatDepts.push(n);
        n.children?.forEach(walkOrg);
      };
      orgTree.forEach(walkOrg);

      const deptsByGroup = new Map<string, any[]>();
      const ungrouped: any[] = [];
      flatDepts.forEach(d => {
        if (d.groupId) {
          if (!deptsByGroup.has(d.groupId)) deptsByGroup.set(d.groupId, []);
          deptsByGroup.get(d.groupId)!.push(d);
        } else {
          ungrouped.push(d);
        }
      });

      const nodes: any[] = [];
      deptGroups
        .filter(g => g.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .forEach(g => {
          const depts = deptsByGroup.get(g.id) ?? [];
          // Đếm tổng task trong nhóm (cộng tất cả khoa của nhóm)
          const groupTaskTotal = depts.reduce((sum, d) => {
            const projs = projectsByDept.get(d.id) ?? [];
            return sum + projs.reduce((s, p) => s + p.taskCount, 0);
          }, 0);
          if (depts.length === 0) return;
          nodes.push({
            key: `grp-${g.id}`,
            title: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <FolderOpen size={14} style={{ color: g.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, flex: 1, color: g.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {g.name}
                </span>
                <span style={{ fontSize: 10, color: '#7d8a98', flexShrink: 0 }}>
                  ({depts.length})
                </span>
                {groupTaskTotal > 0 && (
                  <span style={{
                    background: g.color, color: '#fff', borderRadius: 10,
                    padding: '1px 8px', fontSize: 11, minWidth: 22, textAlign: 'center',
                    fontWeight: 600,
                  }}>
                    {groupTaskTotal}
                  </span>
                )}
              </span>
            ),
            selectable: false,
            children: depts.map(buildDeptNode),
          });
        });
      if (ungrouped.length > 0) {
        nodes.push({
          key: 'grp-none',
          title: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Folder size={14} style={{ color: '#bdc4cd', flexShrink: 0 }} />
              <span style={{ fontWeight: 700, color: '#5b6875' }}>Chưa phân nhóm</span>
              <span style={{ fontSize: 10, color: '#7d8a98' }}>({ungrouped.length})</span>
            </span>
          ),
          selectable: false,
          children: ungrouped.map(buildDeptNode),
        });
      }
      return nodes;
    }

    // Default: top-level departments
    return orgTree.map(buildDeptNode);
  }, [orgTree, projects, groupByDeptGroup, deptGroups]);

  // V1.0.36: Tập key của tất cả node có children — dùng cho nút "Mở rộng tất cả"
  const allExpandKeys = useMemo(() => {
    const keys: React.Key[] = [];
    const walk = (node: any) => {
      if (node.children && node.children.length > 0) {
        keys.push(node.key);
        node.children.forEach(walk);
      }
    };
    treeData.forEach(walk);
    return keys;
  }, [treeData]);

  const [selectedDeptName, setSelectedDeptName] = useState<string | null>(null);
  // Department ID đang highlight trong sider — dùng pre-fill khi tạo project mới
  const [selectedDeptIdForCreate, setSelectedDeptIdForCreate] = useState<string | undefined>(undefined);

  const onSelect = (_selectedKeys: any[], info: any) => {
    const node = info.node;
    const key = node?.key as string | undefined;

    // Click vào dept node (selectable: false → không trigger select bình thường,
    // nhưng vẫn vào onSelect khi expanded). Track dept id để tạo project sau.
    if (key?.startsWith('dept-')) {
      const deptId = key.substring(5);
      setSelectedDeptIdForCreate(deptId);
      return;
    }

    // Click vào project leaf
    if (key?.startsWith('prj-')) {
      const projectId = key.substring(4);
      setSelectedProjectId(projectId);
      const deptName = node?.deptName ?? null;
      setSelectedDeptName(deptName);
      // Track parent dept id của project này
      const proj = projects.find(p => p.id === projectId);
      if (proj?.departmentId) setSelectedDeptIdForCreate(proj.departmentId);
    }
  };

  const archiveMutation = useMutation({
    mutationFn: (id: string) => projectService.archive(id),
    onSuccess: () => {
      message.success('Đã lưu trữ dự án');
      setSelectedProjectId(null);
      setSelectedDeptName(null);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects-archived'] });
    },
    onError: () => message.error('Không lưu trữ được'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectService.delete(id),
    onSuccess: () => {
      message.success('Đã xóa dự án');
      setSelectedProjectId(null);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e: any) => {
      // BE có thể trả về business error nếu project có task
      const code = e?.response?.data?.code;
      const msg = e?.response?.data?.message;
      if (code === 'HAS_TASKS') {
        message.error(msg ?? 'Dự án đã có task — không thể xóa. Hãy lưu trữ thay vì xóa.');
      } else {
        message.error(msg ?? 'Không xóa được. Có thể bạn không phải owner.');
      }
    },
  });

  // Unarchive: đổi status từ ARCHIVED về ACTIVE qua update endpoint
  const unarchiveMutation = useMutation({
    mutationFn: async (id: string) => {
      // Lấy detail để biết name + workflow_mode hiện tại (PATCH yêu cầu nhiều field)
      const detail = await projectService.getById(id);
      return projectService.update(id, {
        name: detail.name,
        description: detail.description ?? undefined,
        color: detail.color ?? undefined,
        icon: detail.icon ?? undefined,
        departmentId: detail.departmentId ?? undefined,
        status: 'ACTIVE',
        workflowMode: detail.workflowMode,
        startDate: detail.startDate ?? undefined,
        endDate: detail.endDate ?? undefined,
      });
    },
    onSuccess: () => {
      message.success('Đã khôi phục dự án');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects-archived'] });
    },
    onError: () => message.error('Không khôi phục được'),
  });

  // Lazy-load archived projects khi user click tab "Lưu trữ"
  const [archivedEnabled, setArchivedEnabled] = useState(false);
  const { data: archivedProjects = [], isLoading: archivedLoading } = useQuery({
    queryKey: ['projects-archived'],
    queryFn: () => projectService.list({ status: 'ARCHIVED' }),
    enabled: archivedEnabled,
  });

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  // Mobile drawer state - tự đóng khi chọn project
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // < 768px = mobile/tablet đứng
  const [mobileSiderOpen, setMobileSiderOpen] = useState(false);

  // Tự đóng drawer khi user chọn project trên mobile
  useEffect(() => {
    if (isMobile && selectedProjectId) {
      setMobileSiderOpen(false);
    }
  }, [selectedProjectId, isMobile]);

  // Sider content - dùng chung cho desktop Sider và mobile Drawer
  const siderContent = (
    <>
      <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ fontSize: 12, color: '#7d8a98', textTransform: 'uppercase' }}>
            Phòng ban / Dự án
          </strong>
          {canCreateProject && (
            <Button
              type="primary"
              size="small"
              icon={<Plus size={12} />}
              onClick={() => setCreateOpen(true)}
            >
              Mới
            </Button>
          )}
        </div>
        <Input
          size="small"
          placeholder="Tìm dự án..."
          prefix={<Search size={12} />}
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          allowClear
        />

        {/* V1.0.32: Toggle gom theo nhóm khoa phòng + V1.0.36: Mở/Gọn tất cả */}
        <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Tooltip title={groupByDeptGroup
            ? 'Đang gom: Nhóm → Khoa → Dự án. Click để bỏ gom.'
            : 'Đang xem: Khoa → Dự án. Click để gom theo Nhóm khoa phòng.'}>
            <Button
              size="small"
              icon={<FolderTree size={12} />}
              type={groupByDeptGroup ? 'primary' : 'default'}
              onClick={() => {
                setGroupByDeptGroup(prev => !prev);
                // Đổi mode → reset expand về null (mặc định expand all theo mode mới)
                setExpandedKeys(null);
              }}
              style={{ fontSize: 11, flex: 1, minWidth: 0 }}
            >
              {groupByDeptGroup ? '✓ Gom nhóm' : 'Gom nhóm'}
            </Button>
          </Tooltip>
          <Tooltip title="Mở rộng tất cả nhóm/khoa">
            <Button
              size="small"
              icon={<ChevronsDownUp size={12} style={{ transform: 'rotate(180deg)' }} />}
              onClick={() => setExpandedKeys(allExpandKeys)}
              style={{ fontSize: 11, padding: '0 6px' }}
            >
              Mở
            </Button>
          </Tooltip>
          <Tooltip title="Thu gọn tất cả">
            <Button
              size="small"
              icon={<ChevronsDownUp size={12} />}
              onClick={() => setExpandedKeys([])}
              style={{ fontSize: 11, padding: '0 6px' }}
            >
              Gọn
            </Button>
          </Tooltip>
        </div>
      </div>

      {projectsLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : treeData.length === 0 ? (
        <Empty
          description="Chưa có dự án"
          style={{ marginTop: 60 }}
          imageStyle={{ height: 60 }}
        />
      ) : (
        <Tree
          blockNode
          treeData={treeData}
          onSelect={onSelect}
          onClick={(_e, node: any) => {
            // Click dept node (selectable: false) → quay về Overview filter theo dept đó
            const key = node?.key as string | undefined;
            if (key?.startsWith('dept-')) {
              const deptId = key.substring(5);
              setSelectedDeptIdForCreate(deptId);
              // Clear selected project để hiện ProjectsOverview thay vì detail panel
              setSelectedProjectId(null);
              setSelectedDeptName(null);
            }
          }}
          selectedKeys={selectedProjectId ? [`prj-${selectedProjectId}`] : []}
          // V1.0.36: controlled expand — null = defaultExpandAll
          {...(expandedKeys === null
            ? { defaultExpandAll: true }
            : { expandedKeys, onExpand: (keys: React.Key[]) => setExpandedKeys(keys) }
          )}
          className="hv-projects-tree"
          style={{ padding: '8px 4px', background: 'transparent' }}
        />
      )}
    </>
  );

  return (
    <Layout className="hv-projects-page" style={{ height: 'calc(100vh - 64px)', background: '#f5f5f5' }}>
      {/* DESKTOP: Sider luôn hiện */}
      {!isMobile && (
        <Sider
          width={280}
          theme="light"
          style={{
            borderRight: '1px solid #e5e5e5',
            overflow: 'auto',
            background: '#fff',
          }}
        >
          {siderContent}
        </Sider>
      )}

      {/* MOBILE: Drawer ẩn/hiện qua nút */}
      {isMobile && (
        <Drawer
          title="Phòng ban / Dự án"
          placement="left"
          width="85%"
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          styles={{ body: { padding: 0 } }}
        >
          {siderContent}
        </Drawer>
      )}

      {/* Content phải: project detail or empty state */}
      <Content style={{ overflow: 'auto', padding: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Mobile-only top bar: nút mở sider + tên project hiện tại */}
        {isMobile && (
          <div style={{
            padding: '8px 12px',
            background: '#fff',
            borderBottom: '1px solid #e5e5e5',
            display: 'flex', alignItems: 'center', gap: 10,
            position: 'sticky', top: 0, zIndex: 5,
          }}>
            <Button
              icon={<MenuIcon size={16} />}
              onClick={() => setMobileSiderOpen(true)}
              size="small"
            >
              Dự án
            </Button>
            {selectedProject && (
              <span style={{
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600,
              }}>
                {selectedProject.name}
              </span>
            )}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {selectedProject ? (
            <ProjectDetailPanel
              projectListItem={selectedProject}
              deptName={selectedDeptName ?? selectedProject.departmentName}
              onArchive={() => archiveMutation.mutate(selectedProject.id)}
              onDelete={() => deleteMutation.mutate(selectedProject.id)}
              onEdit={() => setEditOpen(true)}
              canEdit={isAdmin || selectedProject.ownerId === currentUser?.id}
            />
          ) : (
            <ProjectsOverview
              projects={projects}
              archivedProjects={archivedProjects}
              archivedLoading={archivedLoading}
              onSelect={(id) => {
                setSelectedProjectId(id);
                const p = projects.find(p => p.id === id);
                setSelectedDeptName(p?.departmentName ?? null);
              }}
              onCreate={() => setCreateOpen(true)}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              canCreate={canCreateProject}
              selectedDeptId={selectedDeptIdForCreate}
              deptName={selectedDeptName ?? undefined}
              onUnarchive={(id) => unarchiveMutation.mutate(id)}
              onLoadArchived={() => setArchivedEnabled(true)}
            />
          )}
        </div>
      </Content>

      <CreateProjectModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultDepartmentId={selectedDeptIdForCreate}
        onCreated={async (project) => {
          // Force refetch ngay - chờ data về trước khi đóng modal để user thấy project mới
          await queryClient.invalidateQueries({ queryKey: ['projects'] });
          await queryClient.refetchQueries({ queryKey: ['projects'] });
          // Auto-select project vừa tạo + dept name
          setSelectedProjectId(project.id);
          setSelectedDeptName(project.departmentName ?? null);
          setCreateOpen(false);
          message.success(`Đã tạo dự án "${project.name}"`);
        }}
      />

      {/* V1.0.36: Modal sửa dự án */}
      {selectedProject && (
        <EditProjectModal
          open={editOpen}
          projectId={selectedProject.id}
          onClose={() => setEditOpen(false)}
          onUpdated={async (project) => {
            await queryClient.invalidateQueries({ queryKey: ['projects'] });
            await queryClient.invalidateQueries({ queryKey: ['project-detail', project.id] });
            setSelectedDeptName(project.departmentName ?? null);
            setEditOpen(false);
            // Project chuyển sang ARCHIVED → tự deselect (vì trong list active không còn nữa)
            if (project.status === 'ARCHIVED') {
              setSelectedProjectId(null);
              message.success(`Đã lưu trữ dự án "${project.name}"`);
            } else {
              message.success(`Đã cập nhật dự án "${project.name}"`);
            }
          }}
        />
      )}
    </Layout>
  );
}

// ============================================================
// Overview khi chưa chọn project nào
// ============================================================

function ProjectsOverview({
  projects, archivedProjects, onSelect, onCreate, selectedStatus, onStatusChange,
  canCreate, selectedDeptId, deptName,
  onUnarchive, onLoadArchived, archivedLoading,
}: {
  projects: ProjectListItem[];
  archivedProjects: ProjectListItem[];
  onSelect: (id: string) => void;
  onCreate: () => void;
  selectedStatus: ProjectStatus | undefined;
  onStatusChange: (s: ProjectStatus | undefined) => void;
  canCreate: boolean;
  /** Department đang được chọn trong sider — filter projects theo nó */
  selectedDeptId?: string;
  deptName?: string;
  onUnarchive: (id: string) => void;
  onLoadArchived: () => void;
  archivedLoading: boolean;
}) {
  // Filter theo dept đang chọn (nếu có)
  const visibleProjects = useMemo(() => {
    if (!selectedDeptId) return projects;
    return projects.filter(p => p.departmentId === selectedDeptId);
  }, [projects, selectedDeptId]);

  const stats = useMemo(() => {
    const byStatus: Record<string, number> = {};
    let totalTasks = 0;
    let completedTasks = 0;
    for (const p of visibleProjects) {
      byStatus[p.status] = (byStatus[p.status] ?? 0) + 1;
      totalTasks += p.taskCount;
      completedTasks += p.completedTaskCount;
    }
    return {
      total: visibleProjects.length,
      active: byStatus['ACTIVE'] ?? 0,
      completed: byStatus['COMPLETED'] ?? 0,
      onHold: byStatus['ON_HOLD'] ?? 0,
      draft: byStatus['DRAFT'] ?? 0,
      totalTasks,
      completedTasks,
      progressPct: totalTasks > 0 ? Math.round(100 * completedTasks / totalTasks) : 0,
    };
  }, [visibleProjects]);

  // 4 columns Kanban theo status (DRAFT/ACTIVE/ON_HOLD/COMPLETED)
  const kanbanColumns = useMemo(() => [
    {
      key: 'DRAFT',
      label: 'NHÁP',
      color: '#5b6875',
      bg: '#f0f4f8',
      items: visibleProjects.filter(p => p.status === 'DRAFT'),
    },
    {
      key: 'ACTIVE',
      label: 'ĐANG TRIỂN KHAI',
      color: '#1976D2',
      bg: '#e6f4ff',
      items: visibleProjects.filter(p => p.status === 'ACTIVE'),
    },
    {
      key: 'ON_HOLD',
      label: 'TẠM DỪNG',
      color: '#fa8c16',
      bg: '#fff7e6',
      items: visibleProjects.filter(p => p.status === 'ON_HOLD'),
    },
    {
      key: 'COMPLETED',
      label: 'HOÀN THÀNH',
      color: '#52c41a',
      bg: '#e6ffed',
      items: visibleProjects.filter(p => p.status === 'COMPLETED'),
    },
  ], [visibleProjects]);

  const isArchivedTab = selectedStatus === 'ARCHIVED';

  // V1.0.66: chế độ xem dự án — Bảng (Kanban) hoặc Danh sách.
  // Lưu lựa chọn theo TỪNG NGƯỜI DÙNG vào localStorage để lần sau quay lại vẫn giữ.
  const viewUserId = useAuthStore(s => s.user?.id) ?? 'anon';
  const viewStorageKey = `hv-taskflow-projects-view:${viewUserId}`;
  const [viewMode, setViewMode] = useState<'board' | 'list'>(() => {
    try {
      const saved = localStorage.getItem(viewStorageKey);
      return saved === 'list' || saved === 'board' ? saved : 'board';
    } catch {
      return 'board';
    }
  });
  useEffect(() => {
    try { localStorage.setItem(viewStorageKey, viewMode); } catch { /* ignore */ }
  }, [viewStorageKey, viewMode]);

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
            {selectedDeptId && deptName ? `Dự án — ${deptName}` : 'Quản lý dự án'}
          </h2>
          <div style={{ color: '#7d8a98', fontSize: 13, marginTop: 4 }}>
            {selectedDeptId
              ? `Hiển thị ${visibleProjects.length} dự án thuộc khoa/phòng`
              : 'Tổ chức task theo dự án — Khoa → Quy trình → Tasks'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {!isArchivedTab && (
            <Segmented
              value={viewMode}
              onChange={(v) => setViewMode(v as 'board' | 'list')}
              options={[
                { value: 'board', icon: <LayoutGrid size={14} style={{ verticalAlign: -2 }} />, label: 'Bảng' },
                { value: 'list', icon: <ListIcon size={14} style={{ verticalAlign: -2 }} />, label: 'Danh sách' },
              ]}
            />
          )}
          {canCreate && !isArchivedTab && (
            <Button type="primary" icon={<Plus size={14} />} onClick={onCreate}>
              Tạo dự án mới
            </Button>
          )}
        </div>
      </div>

      {/* Tabs filter trạng thái */}
      <Tabs
        activeKey={selectedStatus ?? 'ALL'}
        onChange={(k) => {
          const newKey = k === 'ALL' ? undefined : (k as ProjectStatus);
          onStatusChange(newKey);
          if (newKey === 'ARCHIVED') onLoadArchived();
        }}
        items={[
          { key: 'ALL', label: `Tất cả (${stats.total})` },
          { key: 'ACTIVE', label: `Đang triển khai (${stats.active})` },
          { key: 'ON_HOLD', label: `Tạm dừng (${stats.onHold})` },
          { key: 'COMPLETED', label: `Hoàn thành (${stats.completed})` },
          { key: 'ARCHIVED', label: `🗄️ Lưu trữ` },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* ARCHIVED tab — list dạng card đơn giản với nút "Khôi phục" */}
      {isArchivedTab ? (
        <ArchivedProjectsList
          projects={archivedProjects}
          loading={archivedLoading}
          onUnarchive={onUnarchive}
          canManage={canCreate}
        />
      ) : (
        /* Kanban view by status */
        <>
          {visibleProjects.length === 0 ? (
            <Empty
              description={selectedDeptId
                ? `Khoa/phòng "${deptName}" chưa có dự án`
                : 'Chưa có dự án nào'}
              style={{ marginTop: 40 }}
            >
              {canCreate && (
                <Button type="primary" icon={<Plus size={14} />} onClick={onCreate}>
                  Tạo dự án đầu tiên
                </Button>
              )}
            </Empty>
          ) : viewMode === 'list' ? (
            <ProjectsListTable projects={visibleProjects} onSelect={onSelect} />
          ) : (
            <div className="hv-projects-kanban" style={{
              display: 'grid',
              gridTemplateColumns: `repeat(4, minmax(240px, 1fr))`,
              gap: 12,
              overflowX: 'auto',
            }}>
              {kanbanColumns.map(col => (
                <div key={col.key} style={{
                  background: '#fff',
                  border: '1px solid #e8edf2',
                  borderTop: `3px solid ${col.color}`,
                  borderRadius: 8,
                  padding: 10,
                  minHeight: 320,
                  display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                  <div style={{
                    padding: '4px 8px',
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                    color: col.color, textTransform: 'uppercase',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span>{col.label}</span>
                    <span style={{
                      background: col.bg, color: col.color,
                      borderRadius: 10, padding: '0 8px',
                      fontSize: 11, minWidth: 22, textAlign: 'center',
                    }}>{col.items.length}</span>
                  </div>

                  {col.items.length === 0 ? (
                    <div style={{
                      padding: 20, textAlign: 'center',
                      fontSize: 11, color: '#bdc4cd',
                    }}>Trống</div>
                  ) : col.items.map(p => (
                    <ProjectCard key={p.id} project={p} onClick={() => onSelect(p.id)} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * V1.0.66: Dự án dạng DANH SÁCH (bảng) — chế độ xem thay cho Kanban.
 */
function ProjectsListTable({ projects, onSelect }: {
  projects: ProjectListItem[];
  onSelect: (id: string) => void;
}) {
  const statusMeta: Record<string, { label: string; color: string }> = {
    DRAFT: { label: 'Nháp', color: 'default' },
    ACTIVE: { label: 'Đang triển khai', color: 'blue' },
    ON_HOLD: { label: 'Tạm dừng', color: 'orange' },
    COMPLETED: { label: 'Hoàn thành', color: 'green' },
    ARCHIVED: { label: 'Lưu trữ', color: 'default' },
  };
  const columns = [
    {
      title: 'Mã', dataIndex: 'code', key: 'code', width: 130,
      render: (code: string | null) => code || '—',
    },
    {
      title: 'Tên dự án', dataIndex: 'name', key: 'name',
      render: (name: string, r: ProjectListItem) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: r.color ?? '#1976D2', flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: '#1a1e23' }}>{name}</span>
        </span>
      ),
    },
    {
      title: 'Khoa/Phòng', dataIndex: 'departmentName', key: 'departmentName',
      render: (d: string | null) => d || '—',
    },
    {
      title: 'Chủ trì', dataIndex: 'ownerName', key: 'ownerName',
      render: (o: string) => o || '—',
    },
    {
      title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 150,
      render: (s: string) => {
        const m = statusMeta[s] ?? { label: s, color: 'default' };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: 'Tiến độ', dataIndex: 'progressPct', key: 'progressPct', width: 170,
      render: (pct: number, r: ProjectListItem) => (
        <Progress percent={pct} size="small" style={{ marginBottom: 0 }} strokeColor={r.color ?? '#1976D2'} />
      ),
    },
    {
      title: 'Task', key: 'tasks', width: 90, align: 'center' as const,
      render: (_: unknown, r: ProjectListItem) => `${r.completedTaskCount}/${r.taskCount}`,
    },
  ];
  return (
    <Table
      rowKey="id"
      dataSource={projects}
      columns={columns}
      pagination={{ pageSize: 20, hideOnSinglePage: true }}
      size="middle"
      onRow={(r) => ({ onClick: () => onSelect(r.id), style: { cursor: 'pointer' } })}
      scroll={{ x: 800 }}
    />
  );
}

/**
 * Tab Lưu trữ — list project đã archive với nút "Khôi phục".
 */
function ArchivedProjectsList({
  projects, loading, onUnarchive, canManage,
}: {
  projects: ProjectListItem[];
  loading: boolean;
  onUnarchive: (id: string) => void;
  canManage: boolean;
}) {
  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  if (projects.length === 0) {
    return (
      <Empty
        description="Chưa có dự án nào trong lưu trữ"
        style={{ marginTop: 40 }}
      />
    );
  }
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: 12,
    }}>
      {projects.map(p => (
        <div key={p.id} style={{
          background: '#fafbfc',
          border: '1px solid #e8edf2',
          borderRadius: 8,
          padding: 14,
          opacity: 0.85,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: p.color ?? '#7d8a98',
            }} />
            <span style={{ fontWeight: 600, color: '#5b6875', flex: 1 }}>{p.name}</span>
            <Tag color="default">Đã lưu trữ</Tag>
          </div>
          <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 8 }}>
            {p.code} · {p.departmentName ?? '—'}
          </div>
          <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 12 }}>
            {p.taskCount} task · {p.completedTaskCount} hoàn thành
          </div>
          {canManage && (
            <Button
              size="small"
              onClick={() => onUnarchive(p.id)}
            >
              ↩ Khôi phục
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, color, icon }: {
  label: string; value: number; color: string; icon: React.ReactNode;
}) {
  return (
    <div style={{
      background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #e5e5e5',
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 8, background: color + '15',
        color, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: '#7d8a98', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 600, color: '#1a1e23' }}>{value}</div>
      </div>
    </div>
  );
}

function ProjectCard({ project, onClick }: {
  project: ProjectListItem; onClick: () => void;
}) {
  const statusColors: Record<string, string> = {
    DRAFT: '#999',
    ACTIVE: '#52c41a',
    ON_HOLD: '#faad14',
    COMPLETED: '#722ed1',
    ARCHIVED: '#d9d9d9',
  };
  const statusLabels: Record<string, string> = {
    DRAFT: 'Bản nháp',
    ACTIVE: 'Đang triển khai',
    ON_HOLD: 'Tạm dừng',
    COMPLETED: 'Hoàn thành',
    ARCHIVED: 'Đã lưu trữ',
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 8, padding: 16, cursor: 'pointer',
        border: '1px solid #e5e5e5', transition: 'all 0.2s',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
        e.currentTarget.style.borderColor = project.color ?? '#1976D2';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = '#e5e5e5';
      }}
    >
      {/* Color stripe top */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 4,
        background: project.color ?? '#1976D2',
        borderRadius: '8px 8px 0 0',
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 600, fontSize: 15, marginBottom: 4,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {project.name}
          </div>
          {project.code && (
            <div style={{ fontSize: 11, color: '#7d8a98', fontFamily: 'monospace' }}>{project.code}</div>
          )}
        </div>
        <Tag color={statusColors[project.status]} style={{ marginRight: 0 }}>
          {statusLabels[project.status]}
        </Tag>
      </div>

      {project.description && (
        <div style={{
          fontSize: 12, color: '#5b6875', marginBottom: 12,
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {project.description}
        </div>
      )}

      {/* Progress */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#7d8a98', marginBottom: 4 }}>
          <span>{project.completedTaskCount}/{project.taskCount} task hoàn thành</span>
          <span>{project.progressPct}%</span>
        </div>
        <Progress
          percent={project.progressPct}
          showInfo={false}
          size="small"
          strokeColor={project.color ?? '#1976D2'}
        />
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#7d8a98' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Users size={12} />
          {project.ownerName}
        </span>
        {project.endDate && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Calendar size={12} />
            {dayjs(project.endDate).format('DD/MM/YYYY')}
          </span>
        )}
      </div>
    </div>
  );
}
